import { useEffect, useState, type FormEvent } from "react";
import type { JourneyMode, Locale, SectionKey, StaffMember, StaffRosterResponse } from "../../shared/dto";
import { confirmForClient, makeSafeT, orderedSections, phaseLabel } from "./core";
import type { StaffPaneProps } from "./console-types";
import type { StaffDetail, StaffRun } from "./model";
export interface ConfirmationUndoTarget {
  kind: "profile" | "apple" | "q1_submit" | "q2_submit" | "knowhow_submit" | "strategy" | "salespage" | "emails" | "webinar";
}

export function confirmationUndoTargets(
  detail: Pick<StaffDetail, "resources" | "deliverables"> & {
    submissions?: StaffDetail["submissions"];
    sections?: StaffDetail["sections"];
  }
): ConfirmationUndoTarget[] {
  const confirmations = detail.resources.find((row) => row.key === "step_confirmations")?.value;
  const confirmationRecord = confirmations && typeof confirmations === "object" && !Array.isArray(confirmations)
    ? confirmations as Record<string, unknown>
    : {};
  const explicitlyConfirmed = (kind: "profile" | "apple") => {
    const value = confirmationRecord[kind];
    return Boolean(value && typeof value === "object" && !Array.isArray(value)
      && (value as Record<string, unknown>).confirmed === true);
  };
  const live = detail.deliverables.filter((row) =>
    (row.published === 1 || row.published === true)
    && ["audit", "plan", "salespage", "emails", "webinar"].includes(String(row.kind)));
  const strategy = live.filter((row) => row.kind === "audit" || row.kind === "plan");
  const approved = (kind: string) => live.some((row) => row.kind === kind && row.status === "approved");
  const submitted = (questionnaire: "q1" | "q2") => (detail.submissions ?? []).some((row) =>
    row.questionnaire === questionnaire
    && ["waiting_for_transcripts", "ready", "exported"].includes(String(row.status)));
  // F104: the know-how confirmation has a way back only while the training
  // window is untouched (no trial link yet) - mirrors the server guard.
  const knowhowReturnable = (detail.sections ?? []).some((section) => String(section.key) === "knowhow" && section.state === "done")
    && (detail.sections ?? []).some((section) => String(section.key) === "training" && section.state === "working")
    && !detail.resources.some((row) => row.key === "trial_link");
  return [
    ...(explicitlyConfirmed("profile") ? [{ kind: "profile" as const }] : []),
    ...(explicitlyConfirmed("apple") ? [{ kind: "apple" as const }] : []),
    ...(submitted("q1") ? [{ kind: "q1_submit" as const }] : []),
    ...(submitted("q2") ? [{ kind: "q2_submit" as const }] : []),
    ...(knowhowReturnable ? [{ kind: "knowhow_submit" as const }] : []),
    ...(strategy.length > 0 && strategy.every((row) => row.status === "approved") ? [{ kind: "strategy" as const }] : []),
    ...(approved("salespage") ? [{ kind: "salespage" as const }] : []),
    ...(approved("emails") ? [{ kind: "emails" as const }] : []),
    ...(approved("webinar") ? [{ kind: "webinar" as const }] : [])
  ];
}

export function ConfirmationUndoPanel(props: {
  clientId: string;
  clientName: string;
  targets: ConfirmationUndoTarget[];
  t: (key: string) => string;
  run: StaffRun;
}) {
  const [armed, setArmed] = useState<ConfirmationUndoTarget["kind"] | null>(null);
  const [busy, setBusy] = useState<ConfirmationUndoTarget["kind"] | null>(null);
  // F104: the way back carries a WRITTEN reason - it becomes the sentence the
  // client reads ("partak ti to vratil k dopracovani: ..."), so the server
  // refuses an empty one and this panel does not offer sending it.
  const [reason, setReason] = useState("");
  if (props.targets.length === 0) return null;

  async function undo(kind: ConfirmationUndoTarget["kind"]) {
    if (reason.trim().length < 3) return;
    setBusy(kind);
    const response = await props.run(
      `/api/staff/clients/${props.clientId}/confirmations/${kind}/undo`,
      { method: "POST", body: { reason: reason.trim() } }
    );
    if (response.ok) {
      setArmed(null);
      setReason("");
    }
    setBusy(null);
  }

  return (
    <article className="staff-card confirmation-undo-card">
      <h3>{props.t("staffConsole.confirmationUndo.title")}</h3>
      <p className="staff-card-hint">{props.t("staffConsole.confirmationUndo.hint")}</p>
      <ul>
        {props.targets.map((target) => {
          const label = props.t(`staffConsole.confirmationUndo.kind.${target.kind}`);
          const confirmCopy = props.t("staffConsole.confirmationUndo.confirm")
            .replace("{client}", props.clientName)
            .replace("{item}", label);
          return (
            <li key={target.kind}>
              <span><strong>{label}</strong><small>{props.t("staffConsole.confirmationUndo.confirmed")}</small></span>
              {armed === target.kind ? (
                <div className="confirmation-undo-confirm" role="alert">
                  <p>{confirmCopy}</p>
                  <label className="override-reason">
                    <span>{props.t("staffConsole.override.reasonLabel")}</span>
                    <textarea
                      rows={2}
                      value={reason}
                      placeholder={props.t("staffConsole.override.reasonPlaceholder")}
                      onChange={(event) => setReason(event.target.value)}
                    />
                  </label>
                  <div className="button-row">
                    <button
                      className="secondary-button compact-button"
                      type="button"
                      disabled={busy === target.kind || reason.trim().length < 3}
                      title={reason.trim().length < 3 ? props.t("staffConsole.override.reasonRequired") : undefined}
                      onClick={() => void undo(target.kind)}
                    >
                      {props.t("staffConsole.confirmationUndo.confirmButton")}
                    </button>
                    <button className="quiet-link" type="button" disabled={busy === target.kind} onClick={() => { setArmed(null); setReason(""); }}>
                      {props.t("common.cancel")}
                    </button>
                  </div>
                  {reason.trim().length < 3 && (
                    <p className="override-locked-note">{props.t("staffConsole.override.reasonRequired")}</p>
                  )}
                </div>
              ) : (
                <button
                  className="quiet-link confirmation-undo-open"
                  type="button"
                  title={confirmCopy}
                  onClick={() => { setArmed(target.kind); setReason(""); }}
                >
                  {props.t("staffConsole.confirmationUndo.button")}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </article>
  );
}

export function StableCallLinksCard(props: {
  callLinks: { onboarding: string; marketing: string };
  t: (key: string) => string;
}) {
  return (
    <article className="staff-card stable-call-links">
      <h3>{props.t("staffConsole.callLinks.title")}</h3>
      <p className="staff-card-hint">{props.t("staffConsole.callLinks.hint")}</p>
      <dl className="staff-definition">
        <div>
          <dt>{props.t("staffConsole.callLinks.onboarding")}</dt>
          <dd><code>{props.callLinks.onboarding}</code></dd>
        </div>
        <div>
          <dt>{props.t("staffConsole.callLinks.marketing")}</dt>
          <dd><code>{props.callLinks.marketing}</code></dd>
        </div>
      </dl>
    </article>
  );
}

// F138e: ClientAccessActions is GONE from Technické údaje. Both of its
// controls moved to the postranní sloupec (ClientContext), where the
// question they answer is asked: "Otevřít jako klient" was buried four
// levels deep and admin only, and the login link belongs beside it. One
// capability, one home - a second copy here is what F138 came to delete.

export function RegressionEditor(props: StaffPaneProps) {
  const candidates = orderedSections(props.detail.sections).filter((section) => section.key !== "home" && section.state !== "locked");
  const [sectionKey, setSectionKey] = useState<SectionKey>(candidates[0]?.key ?? "onboarding_call");
  const [targetState, setTargetState] = useState<"locked" | "next" | "working">("locked");
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const section = props.detail.sections.find((item) => item.key === sectionKey);
  const ranks = { locked: 0, next: 1, working: 2, done: 3 } as const;
  const allowed = Boolean(section && ranks[targetState] < ranks[section.state] && reason.trim().length >= 10 && confirmation === props.detail.client.name);
  async function regress(event: FormEvent) {
    event.preventDefault();
    if (!section || !allowed) return;
    const next = Object.fromEntries(props.detail.sections.map((item) => [item.key, { state: item.key === section.key ? targetState : item.state, primaryNext: false }]));
    const priority: SectionKey[] = ["onboarding_call", "q1", "knowhow", "beta", "marketing_call", "q2", "handover", "app"];
    const nextPrimary = priority.find((key) => next[key].state === "next");
    if (nextPrimary) next[nextPrimary].primaryNext = true;
    const preview = props.detail.sections.filter((item) => item.state !== next[item.key].state || item.primaryNext !== next[item.key].primaryNext)
      .map((item) => ({ sectionKey: item.key, state: item.state, rowVersion: item.rowVersion }));
    await props.run(`/api/staff/clients/${props.detail.client.id}/sections/${section.key}/transition`, {
      method: "POST",
      body: { direction: "regression", targetState, rowVersion: section.rowVersion, reason, confirmClientName: confirmation, preview }
    });
  }
  return <form className="staff-card editor-form danger-zone" onSubmit={regress}>
    <h3>{props.t("staffConsole.regression")}</h3>
    <p>{props.t("staffConsole.regressionWarning")}</p>
    <select value={sectionKey} onChange={(event) => setSectionKey(event.target.value as SectionKey)}>{candidates.map((item) => <option key={item.key} value={item.key}>{phaseLabel(item.key)}</option>)}</select>
    <select value={targetState} onChange={(event) => setTargetState(event.target.value as typeof targetState)}><option value="locked">{props.t("staffConsole.state.locked")}</option><option value="next">{props.t("staffConsole.state.next")}</option><option value="working">{props.t("staffConsole.state.working")}</option></select>
    <input value={reason} onChange={(event) => setReason(event.target.value)} placeholder={props.t("staffConsole.reasonLong")} />
    <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder={props.t("staffConsole.typeClientName")} />
    <button className="text-button danger" type="submit" disabled={!allowed}>{props.t("staffConsole.confirmRegression")}</button>
  </form>;
}

export function MilestoneEditor(props: StaffPaneProps) {
  const [key, setKey] = useState("telegram_launch");
  const [evidence, setEvidence] = useState('{"verified":true}');
  async function save(event: FormEvent) {
    event.preventDefault();
    let value: unknown;
    try { value = JSON.parse(evidence); } catch { return; }
    await props.run(`/api/admin/clients/${props.detail.client.id}/milestones/${key}`, { method: "PUT", body: { reachedAt: new Date().toISOString(), evidence: value } });
  }
  async function remove() {
    if (!confirmForClient(props, "staffConsole.confirmMilestoneDelete")) return;
    await props.run(`/api/admin/clients/${props.detail.client.id}/milestones/${key}`, { method: "DELETE", body: { reason: props.t("staffConsole.milestoneDeleteReason") } });
  }
  return <form className="staff-card editor-form" onSubmit={save}>
    <h3>{props.t("staffConsole.milestoneEditor")}</h3>
    <select value={key} onChange={(event) => setKey(event.target.value)}>{milestoneKeys.map((item) => <option key={item} value={item}>{makeSafeT(props.t)(`handover.proof.${item}`, item.replaceAll("_", " "))}</option>)}</select>
    <textarea value={evidence} onChange={(event) => setEvidence(event.target.value)} rows={4} />
    <div className="button-row"><button className="primary-button" type="submit">{props.t("staffConsole.save")}</button><button className="text-button danger" type="button" onClick={() => void remove()}>{props.t("staffConsole.delete")}</button></div>
  </form>;
}

export function ClientEditor(props: StaffPaneProps) {
  const client = props.detail.client;
  const [partners, setPartners] = useState<StaffMember[]>([]);
  const [values, setValues] = useState({
    name: client.name,
    marketLanguage: client.locale,
    includesApp: client.axes.includesApp,
    purchaseSource: client.axes.purchaseSource,
    instanceStartMode: client.axes.instanceStartMode,
    // F143: ALWAYS sent, never omitted. This route is a blind whole-row write,
    // so a form that left the field out would move a sales_only client back to
    // the full journey on the next unrelated save.
    journeyMode: client.axes.journeyMode,
    // F64: the empty option is a real answer ("we do not know"), not a blank
    // field - it stores NULL and the client reads the neutral "spustil/a".
    gender: client.axes.gender ?? "",
    partnerId: client.partner?.id ?? "",
    ownerId: client.owner?.id ?? ""
  });
  useEffect(() => {
    void (async () => {
      try {
        const response = await fetch("/api/admin/staff", { credentials: "include" });
        if (!response.ok) return;
        const body = await response.json() as StaffRosterResponse;
        setPartners(body.members.filter((member) => member.isActive));
      } catch {
        // Fall back to the current assignment; the select still shows it.
      }
    })();
  }, []);
  async function save(event: FormEvent) {
    event.preventDefault();
    await props.run(`/api/admin/clients/${client.id}`, { method: "PATCH", body: values });
  }
  // The assigned partner may not (yet) be in the active roster; keep it selectable.
  const partnerOptions = partners.some((member) => member.id === values.partnerId) || !client.partner
    ? partners
    : [{ id: client.partner.id, email: client.partner.name ?? client.partner.id, displayName: client.partner.name, role: "partner", isActive: true, clientCount: 0, lastSeenAt: null, profileComplete: false, createdAt: "", self: false } as StaffMember, ...partners];
  return <form className="staff-card editor-form" onSubmit={save} data-admin-tool="edit-client">
    <h3>{props.t("staffConsole.editClient")}</h3>
    <label className="inline-select"><span>{props.t("staffConsole.clientName")}</span>
      <input value={values.name} onChange={(event) => setValues({ ...values, name: event.target.value })} required /></label>
    <div className="inline-fields">
      <label className="inline-select"><span>{props.t("staffConsole.callLocale")}</span>
        <select value={values.marketLanguage} onChange={(event) => setValues({ ...values, marketLanguage: event.target.value as Locale })}><option value="cs">{props.t("common.languageCs")}</option><option value="en">{props.t("common.languageEn")}</option></select></label>
      <label className="inline-select"><span>{props.t("staffConsole.purchaseMode")}</span>
        <select value={values.purchaseSource} onChange={(event) => setValues({ ...values, purchaseSource: event.target.value })}><option value="direct">{props.t("staffConsole.source.direct")}</option><option value="upsell">{props.t("staffConsole.source.upsell")}</option><option value="webinar">{props.t("staffConsole.source.webinar")}</option></select></label>
    </div>
    <div className="inline-fields">
      <label className="inline-select"><span>{props.t("staffConsole.instanceMode")}</span>
        <select value={values.instanceStartMode} onChange={(event) => setValues({ ...values, instanceStartMode: event.target.value })}><option value="new">{props.t("staffConsole.start.new")}</option><option value="existing">{props.t("staffConsole.start.existing")}</option></select></label>
      <label className="inline-select"><span>{props.t("staffConsole.clientGender")}</span>
        <select value={values.gender} onChange={(event) => setValues({ ...values, gender: event.target.value })}><option value="">{props.t("staffConsole.gender.unknown")}</option><option value="f">{props.t("staffConsole.gender.female")}</option><option value="m">{props.t("staffConsole.gender.male")}</option></select></label>
    </div>
    {/* F143: the axis that decides how many steps the client walks. It has its
        own row with its own sentence, because it is the one field here that
        REMOVES eight steps from somebody's journey. */}
    <label className="inline-select"><span>{props.t("staffConsole.journeyMode")}</span>
      <select value={values.journeyMode} onChange={(event) => setValues({ ...values, journeyMode: event.target.value as JourneyMode })}>
        <option value="full">{props.t("staffConsole.journey.full")}</option>
        <option value="sales_only">{props.t("staffConsole.journey.sales_only")}</option>
      </select></label>
    <p className="staff-card-hint">{props.t("staffConsole.journeyHint")}</p>
    {/* F139: the empty option is a real answer, so the select is no longer
        `required`. A client with no partner waits in the admin's unassigned
        queue and reads the working state in their own right column. */}
    <label className="inline-select"><span>{props.t("staffConsole.partner")}</span>
      <select value={values.partnerId} onChange={(event) => setValues({ ...values, partnerId: event.target.value })}>
        <option value="">{props.t("staffConsole.team.assignLater")}</option>
        {partnerOptions.map((member) => <option key={member.id} value={member.id}>{member.displayName ?? member.email}</option>)}
      </select></label>
    <label className="inline-select"><span>{props.t("staffConsole.ownerId")}</span>
      <input value={values.ownerId} onChange={(event) => setValues({ ...values, ownerId: event.target.value })} required /></label>
    <label className="checkbox-row"><input type="checkbox" checked={values.includesApp} onChange={(event) => setValues({ ...values, includesApp: event.target.checked })} /><span>{props.t("staffConsole.includesApp")}</span></label>
    <button className="primary-button" type="submit">{props.t("staffConsole.save")}</button>
  </form>;
}
export const milestoneKeys = ["telegram_launch", "bot_transferred", "stripe_receiving", "assets_handed", "first_campaign_or_trial", "app_live"];
